// Admin Studio Media. Curated artwork the studio offers customers in the
// Designer's Media Library picker (files.kind='studio_media'). Two-column
// layout: folder sidebar on the left, file grid on the right. Search input
// at the top sweeps the whole library; the active folder narrows the grid.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../../lib/api'

interface Asset {
  id: string; originalName: string; mimeType: string; size: number
  widthPx: number | null; heightPx: number | null
  folderId: string | null
  url: string; createdAt: string
}
interface Folder {
  id: string; name: string; sortOrder: number; createdAt: string; count: number
}
interface FoldersResponse { folders: Folder[]; rootCount: number; totalCount: number }

// Sentinel values used by the API + UI for the "All" and "Unfiled" buckets.
const ALL  = 'all'   as const
const ROOT = 'root'  as const

type ActiveFolder = typeof ALL | typeof ROOT | string

export default function StudioMedia() {
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const [items, setItems] = useState<Asset[]>([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [rootCount, setRootCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [err, setErr] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [folder, setFolder] = useState<ActiveFolder>(ALL)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [moveFor, setMoveFor] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  // Debounce search input → server query so we don't spam the API while typing.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const loadFolders = useCallback(async () => {
    try {
      const d = await apiFetch<FoldersResponse>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/studio-media/folders')
      setFolders(d.folders); setRootCount(d.rootCount); setTotalCount(d.totalCount)
    } catch (e) { setErr((e as Error).message) }
  }, [getAccessToken])

  const loadItems = useCallback(async () => {
    try {
      const qs = new URLSearchParams()
      qs.set('folderId', folder)
      if (search) qs.set('search', search)
      const d = await apiFetch<{ items: Asset[] }>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/studio-media?${qs.toString()}`)
      setItems(d.items); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }, [getAccessToken, folder, search])

  useEffect(() => { void loadFolders() }, [loadFolders])
  useEffect(() => { void loadItems() }, [loadItems])

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const fs = Array.from(e.target.files ?? [])
    if (!fs.length) return
    // New uploads land in the active folder when one is selected; ALL/ROOT
    // both default to root (folderId=null) so the file shows under "Unfiled".
    const uploadFolderId: string | null = folder === ALL || folder === ROOT ? null : folder
    setUploading(true)
    try {
      for (const f of fs) {
        await new Promise<void>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = async () => {
            try {
              await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/files', {
                method: 'POST',
                body: JSON.stringify({ originalName: f.name, mimeType: f.type, base64: reader.result, kind: 'studio_media', folderId: uploadFolderId }),
              })
              resolve()
            } catch (err) { reject(err) }
          }
          reader.onerror = () => reject(reader.error)
          reader.readAsDataURL(f)
        })
      }
      await Promise.all([loadFolders(), loadItems()])
    } catch (err) { alert((err as Error).message) }
    finally { setUploading(false); e.target.value = '' }
  }

  async function createFolder() {
    const name = prompt('Folder name?')?.trim()
    if (!name) return
    try {
      const r = await apiFetch<{ folder: Folder }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/studio-media/folders', {
        method: 'POST', body: JSON.stringify({ name }),
      })
      await loadFolders()
      setFolder(r.folder.id)
    } catch (e) { alert((e as Error).message) }
  }

  async function renameFolder(f: Folder) {
    const name = prompt('Rename folder', f.name)?.trim()
    if (!name || name === f.name) return
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/studio-media/folders/${f.id}`, {
        method: 'PATCH', body: JSON.stringify({ name }),
      })
      await loadFolders()
    } catch (e) { alert((e as Error).message) }
  }

  async function deleteFolder(f: Folder) {
    if (!confirm(`Delete folder "${f.name}"? ${f.count} file${f.count === 1 ? '' : 's'} inside will be moved to Unfiled.`)) return
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/studio-media/folders/${f.id}`, { method: 'DELETE' })
      if (folder === f.id) setFolder(ALL)
      await Promise.all([loadFolders(), loadItems()])
    } catch (e) { alert((e as Error).message) }
  }

  async function moveTo(id: string, folderId: string | null) {
    setMoveFor(null)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/studio-media/${id}/move`, {
        method: 'PATCH', body: JSON.stringify({ folderId }),
      })
      await Promise.all([loadFolders(), loadItems()])
    } catch (e) { alert((e as Error).message) }
  }

  async function remove(id: string) {
    if (!confirm('Remove this asset from the Media Library? Existing designs that reference it will keep working.')) return
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/studio-media/${id}`, { method: 'DELETE' })
      await Promise.all([loadFolders(), loadItems()])
    } catch (e) { alert((e as Error).message) }
  }

  const activeName = folder === ALL ? 'All files'
                    : folder === ROOT ? 'Unfiled'
                    : (folders.find((f) => f.id === folder)?.name ?? 'Folder')

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Catalog</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Media Library</h1>
          <p className="text-ink-2 text-sm mt-1 max-w-2xl">Curated artwork the studio offers customers in the Designer&apos;s Media Library picker. Organise into folders, search by filename.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search filename..."
            className="input max-w-xs"
          />
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-gold">{uploading ? 'Uploading…' : '+ Upload artwork'}</button>
        </div>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Folder sidebar */}
        <aside className="space-y-1">
          <FolderBtn label="All files"  count={totalCount} active={folder === ALL}  onClick={() => setFolder(ALL)} />
          <FolderBtn label="Unfiled"    count={rootCount}  active={folder === ROOT} onClick={() => setFolder(ROOT)} />
          <div className="border-t border-border my-2" />
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3">Folders</span>
            <button onClick={createFolder} className="text-[11px] text-gold-dark hover:underline" title="Create a new folder">+ New</button>
          </div>
          {folders.length === 0 ? (
            <p className="text-[11px] text-ink-3 px-2 py-1">No folders yet. Click + New to add one.</p>
          ) : (
            folders.map((f) => {
              const active = folder === f.id
              return (
                <div key={f.id}
                  className={'group flex items-center gap-1 rounded transition-colors ' +
                    (active ? 'bg-gold/15' : 'hover:bg-surface')}>
                  <button onClick={() => setFolder(f.id)}
                    className={'flex-1 min-w-0 flex items-center justify-between gap-2 text-left px-3 py-2 text-sm ' +
                      (active ? 'text-ink' : 'text-ink-2')}>
                    <span className="truncate">{f.name}</span>
                    <span className={'text-[11px] shrink-0 ' + (active ? 'text-gold-dark' : 'text-ink-3')}>{f.count}</span>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); renameFolder(f) }}
                    title="Rename folder" aria-label={`Rename ${f.name}`}
                    className="shrink-0 w-6 h-6 rounded text-[11px] text-ink-3 hover:text-ink hover:bg-white border border-transparent hover:border-border flex items-center justify-center">✎</button>
                  <button onClick={(e) => { e.stopPropagation(); deleteFolder(f) }}
                    title="Delete folder" aria-label={`Delete ${f.name}`}
                    className="shrink-0 w-6 h-6 mr-1 rounded text-[12px] text-ink-3 hover:text-red-600 hover:bg-white border border-transparent hover:border-red-200 flex items-center justify-center">×</button>
                </div>
              )
            })
          )}
        </aside>

        {/* File grid */}
        <div>
          <div className="text-xs text-ink-3 mb-3 flex items-center justify-between">
            <span><strong className="text-ink-2">{activeName}</strong> · {items.length} file{items.length === 1 ? '' : 's'}{search ? <> · matching <strong className="text-ink-2">"{search}"</strong></> : null}</span>
            {search && (
              <button onClick={() => { setSearch(''); setSearchInput('') }} className="text-[11px] text-ink-3 hover:text-ink">Clear search</button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="card text-center text-ink-3 text-sm py-12">
              {search ? <>No matches for &ldquo;<strong className="text-ink-2">{search}</strong>&rdquo; in {activeName}.</>
                      : <>No assets in this view. Click <strong className="text-ink">+ Upload artwork</strong> to add some.</>}
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {items.map((a) => (
                <div key={a.id} className="group relative">
                  <div className="aspect-square bg-surface border border-border rounded overflow-hidden flex items-center justify-center">
                    <img src={apiSrc(a.url)} alt={a.originalName} className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="text-[11px] text-ink-2 mt-1 truncate" title={a.originalName}>{a.originalName}</div>

                  {/* Hover actions: move-to + delete */}
                  <div className="absolute top-1 right-1 flex flex-col items-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setMoveFor((cur) => cur === a.id ? null : a.id)} title="Move to folder"
                      className="w-6 h-6 bg-white/90 border border-border rounded text-ink-3 hover:text-ink flex items-center justify-center text-[12px]">⇄</button>
                    <button onClick={() => remove(a.id)} title="Remove"
                      className="w-6 h-6 bg-white/90 border border-border rounded text-ink-3 hover:text-red-600 flex items-center justify-center">×</button>
                  </div>

                  {moveFor === a.id && (
                    <div className="absolute top-9 right-1 z-10 bg-white border border-border rounded shadow-lg w-40 py-1 text-xs">
                      <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-ink-3 border-b border-border">Move to</div>
                      <button onClick={() => moveTo(a.id, null)}
                        className={'w-full text-left px-3 py-1.5 hover:bg-surface ' + (a.folderId == null ? 'text-ink font-semibold' : 'text-ink-2')}>
                        Unfiled
                      </button>
                      {folders.map((f) => (
                        <button key={f.id} onClick={() => moveTo(a.id, f.id)}
                          className={'w-full text-left px-3 py-1.5 hover:bg-surface truncate ' + (a.folderId === f.id ? 'text-ink font-semibold' : 'text-ink-2')}>
                          {f.name}
                        </button>
                      ))}
                      <div className="border-t border-border mt-1 pt-1">
                        <button onClick={() => setMoveFor(null)} className="w-full text-left px-3 py-1.5 text-ink-3 hover:bg-surface">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function FolderBtn({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className={'w-full flex items-center justify-between gap-2 text-left px-3 py-2 text-sm rounded transition-colors ' +
        (active ? 'bg-gold/15 text-ink' : 'text-ink-2 hover:bg-surface')}>
      <span className="truncate">{label}</span>
      <span className={'text-[11px] shrink-0 ' + (active ? 'text-gold-dark' : 'text-ink-3')}>{count}</span>
    </button>
  )
}
